function emptySizeGuide() {
  return {
    enabled: false,
    groups: [],
    columns: [],
    rows: [],
  }
}

const list = value => (Array.isArray(value) ? value : [])
const text = value => (value != null ? String(value) : '')

export function isSizeGuideRecord(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && ['groups', 'columns', 'rows'].every(key => Array.isArray(value[key]))
}

export function createStableId(prefix = 'sg') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

function uniqueId(preferred, prefix, used) {
  let id = preferred != null && String(preferred) !== '' ? String(preferred) : createStableId(prefix)
  while (used.has(id)) id = createStableId(prefix)
  used.add(id)
  return id
}

function normalizeCellValue(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  return value
}

function cellsFor(columns, source = {}) {
  const cells = {}
  columns.forEach(column => {
    cells[column.id] = Object.prototype.hasOwnProperty.call(source, column.id)
      ? normalizeCellValue(source[column.id])
      : null
  })
  return cells
}

function normalizeColumns(columns, usedIds) {
  return list(columns).map(column => ({
    id: uniqueId(column?.id, 'c', usedIds),
    name: text(column?.name),
  }))
}

function normalizeRows(rows, columns, usedIds) {
  return list(rows).map(row => ({
    id: uniqueId(row?.id, 'r', usedIds),
    cells: cellsFor(columns, row?.cells && typeof row.cells === 'object' ? row.cells : {}),
  }))
}

function normalizeGroups(groups, columns, usedIds) {
  const columnIds = new Set(columns.map(column => column.id))
  const columnOrder = columns.map(column => column.id)
  const claimed = new Set()
  return list(groups).map(group => {
    const ids = []
    list(group?.column_ids).forEach(rawId => {
      const id = String(rawId)
      if (!columnIds.has(id) || claimed.has(id)) return
      claimed.add(id)
      ids.push(id)
    })
    return {
      id: uniqueId(group?.id, 'g', usedIds),
      name: text(group?.name),
      column_ids: columnOrder.filter(id => ids.includes(id)),
    }
  })
}

export function normalizeSizeGuide(raw) {
  if (!raw || typeof raw !== 'object') return emptySizeGuide()
  const usedIds = new Set()
  const columns = normalizeColumns(raw.columns, usedIds)
  return {
    enabled: !!raw.enabled,
    groups: normalizeGroups(raw.groups, columns, usedIds),
    columns,
    rows: normalizeRows(raw.rows, columns, usedIds),
  }
}

export function createColumn() {
  return { id: createStableId('c'), name: '' }
}

export function createRow(columns = []) {
  return { id: createStableId('r'), cells: cellsFor(columns) }
}

export function createGroup() {
  return { id: createStableId('g'), name: '', column_ids: [] }
}

export function addColumnToGuide(guide) {
  const column = createColumn()
  return {
    ...guide,
    columns: [...list(guide.columns), column],
    rows: list(guide.rows).map(row => ({
      ...row,
      cells: { ...(row.cells || {}), [column.id]: null },
    })),
  }
}

export function removeColumnFromGuide(guide, columnId) {
  return {
    ...guide,
    columns: list(guide.columns).filter(column => column.id !== columnId),
    rows: list(guide.rows).map(row => {
      const cells = { ...(row.cells || {}) }
      delete cells[columnId]
      return { ...row, cells }
    }),
    groups: list(guide.groups).map(group => ({
      ...group,
      column_ids: list(group.column_ids).filter(id => id !== columnId),
    })),
  }
}

export function addRowToGuide(guide) {
  return { ...guide, rows: [...list(guide.rows), createRow(guide.columns)] }
}

export function removeRowFromGuide(guide, rowId) {
  return { ...guide, rows: list(guide.rows).filter(row => row.id !== rowId) }
}

export function addGroupToGuide(guide) {
  return { ...guide, groups: [...list(guide.groups), createGroup()] }
}

export function removeGroupFromGuide(guide, groupId) {
  return { ...guide, groups: list(guide.groups).filter(group => group.id !== groupId) }
}

export function alignGroupColumnIds(guide) {
  const columnOrder = list(guide.columns).map(column => column.id)
  return {
    ...guide,
    groups: list(guide.groups).map(group => ({
      ...group,
      column_ids: columnOrder.filter(id => list(group.column_ids).includes(id)),
    })),
  }
}

export function assignColumnGroup(guide, columnId, groupId) {
  const groups = list(guide.groups).map(group => {
    const columnIds = list(group.column_ids).filter(id => id !== columnId)
    if (groupId && group.id === groupId) columnIds.push(columnId)
    return { ...group, column_ids: columnIds }
  })
  return alignGroupColumnIds({ ...guide, groups })
}

export function getColumnGroupId(guide, columnId) {
  const group = list(guide.groups).find(item => list(item.column_ids).includes(columnId))
  return group ? group.id : ''
}

export function prepareSizeGuideForSubmit(guide) {
  const normalized = alignGroupColumnIds(normalizeSizeGuide(guide))
  const columns = normalized.columns.map(column => ({
    id: column.id,
    name: column.name.trim(),
  }))
  return {
    enabled: !!normalized.enabled,
    groups: normalized.groups
      .map(group => ({
        id: group.id,
        name: group.name.trim(),
        column_ids: group.column_ids,
      }))
      .filter(group => group.column_ids.length),
    columns,
    rows: normalized.rows.map(row => ({
      id: row.id,
      cells: cellsFor(columns, row.cells || {}),
    })),
  }
}

export function withSizeGuideRequestPayload(payload) {
  if (!payload) return payload
  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    if (!payload.has('size_guide')) return payload
    const value = payload.get('size_guide')
    if (value == null || value === '') payload.delete('size_guide')
    else if (typeof value !== 'string') payload.set('size_guide', JSON.stringify(value))
    return payload
  }
  if (!Object.prototype.hasOwnProperty.call(payload, 'size_guide')) return payload
  if (payload.size_guide == null || payload.size_guide === '') {
    const next = { ...payload }
    delete next.size_guide
    return next
  }
  return payload
}

export function validateSizeGuide(guide) {
  const {
    enabled, columns, rows, groups,
  } = prepareSizeGuideForSubmit(guide)
  if (enabled && !columns.length) return 'size-guide.column-required'
  if (enabled && !rows.length) return 'size-guide.row-required'
  if (columns.some(column => !column.name)) return 'size-guide.column-name-required'
  if (groups.some(group => !group.name)) return 'size-guide.grouped-header-required'
  return ''
}

export function buildGroupHeaderCells(columns, groups) {
  const cols = list(columns)
  const groupByColId = {}
  list(groups).forEach(group => {
    list(group.column_ids).forEach(id => { groupByColId[id] = group })
  })
  const cells = []
  for (let index = 0; index < cols.length;) {
    const column = cols[index]
    const group = groupByColId[column.id]
    if (!group) {
      cells.push({
        type: 'ungrouped',
        column,
        colspan: 1,
        rowspan: 2,
      })
      index += 1
    } else {
      let span = 1
      while (index + span < cols.length && groupByColId[cols[index + span].id] === group) {
        span += 1
      }
      cells.push({
        type: 'group',
        group,
        colspan: span,
        rowspan: 1,
      })
      index += span
    }
  }
  return cells
}
